import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { AxiosError, AxiosInstance } from 'axios';
import { UseCountriesProps, UseCountriesQueryKeyProps } from './props';
import { fetchCountries } from './service';
import { useMemo } from 'react';
import {
  useAxiosInstance,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { CountriesQueryKeys } from '../../queryKeys';
import { FetchCountriesInputDto } from '@starsoft/common/dto';
import {
  Country,
  GenericError,
  PaginatedResponse,
} from '@starsoft/common/models';

/**
 * Custom hook to fetch paginated country data.
 *
 * This hook utilizes the `useInfiniteQuery` from React Query to fetch and manage
 * paginated data for countries. It handles the query key, pagination, and data formatting.
 *
 * @param {UseCountriesProps} options - Configuration options for the query.
 * @param {boolean} [options.enabled=true] - Flag to enable or disable the query.
 * @param {string} [options.query] - Query string to filter countries.
 * @param {string} [options.originId] - Origin ID to filter countries.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {boolean} return.isFetching - Indicates if the query is currently loading.
 * @returns {boolean} return.hasNextPage - Indicates if there are more pages to fetch.
 * @returns {AxiosError<GenericError>} return.error - Error object if the query fails.
 * @returns {Function} return.fetchNextPage - Function to fetch the next page of data.
 * @returns {Country[]} return.countries - Flattened array of country data from all pages.
 */
export function useCountries(options?: UseCountriesProps) {
  // Destructure options with default values
  const { enabled = true, query, originId } = options ?? {};

  // Define the filter parameters for the query
  const filter: FetchCountriesInputDto = {
    limit: 50,
    query,
    originId,
  };

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Define the query key for caching and refetching purposes
  const queryKey: UseCountriesQueryKeyProps = [
    CountriesQueryKeys.Countries,
    filter,
  ];

  // Get functions to handle pagination
  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  // Reset the infinite query state
  useInfiniteQueryReset(queryKey);

  // Use the useInfiniteQuery hook to fetch paginated country data
  const {
    data,
    isFetching,
    hasNextPage,
    error,
    fetchNextPage,
    ...infiniteQuery
  } = useInfiniteQuery<
    PaginatedResponse<Country>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<Country>>,
    UseCountriesQueryKeyProps
  >({
    queryKey,
    queryFn: queryContext => fetchCountries(queryContext, axiosInstance),
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam: 1,
    enabled,
  });

  // Memoize the flattened array of country data from all pages
  const countries = useMemo(
    () => data?.pages?.flatMap(page => page.data),
    [data],
  );

  // Return the query state and functions
  return {
    ...infiniteQuery,
    isFetching, // Indicates if the query is currently loading
    hasNextPage, // Indicates if there are more pages to fetch
    error, // Error object if the query fails
    fetchNextPage, // Function to fetch the next page of data
    countries, // Flattened array of country data from all pages
  };
}
